import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, Plus, Trash } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { COMMODITY_CATEGORIES, RESTRICTED_FLAGS } from "../../lib/constants";
import { PhoneField } from "../../components/PhoneField";
import { friendlyError } from "../admin/shipment-detail/_shared";
import { AddressFields, Field, Row, Section, PACKAGE_TYPES, LOAD_ASSIST_OPTIONS } from "./NewBooking";

const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n || 0));
const clone = (o) => JSON.parse(JSON.stringify(o));
const blankAddr = { line1: "", city: "", state: "", pincode: "", landmark: "" };

// Pre-MAIN commercial "Edit booking" for the owning Sender. Uses ONLY the existing revise-draft
// contract (preview = rolled-back dry run of the same service). The sender contact mobile is never
// sent here (separate contact correction), so a corrected mobile can't be overwritten.
export default function ReviseBooking() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ship, setShip] = useState(null);
  const [rev, setRev] = useState(null);
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState("");
  const [avail, setAvail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewedFor, setPreviewedFor] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (isAlive = () => true) => {
    setError(null); setPreview(null);
    try {
      const { data } = await api.get(`/shipments/${id}`);
      if (!isAlive()) return;
      const r = data.revision;
      setShip(data.shipment); setRev(r);
      if (r?.draft) {
        const d = clone(r.draft);
        d.pickup_address = { ...blankAddr, ...(d.pickup_address || {}) };
        d.receiver.delivery_address = { ...blankAddr, ...(d.receiver.delivery_address || {}) };
        setDraft(d); setOriginal(JSON.stringify(d));
      }
    } catch (e) { setError({ message: friendlyError(e, "We couldn't load this booking.") }); }
  }, [id]);
  useEffect(() => {
    let alive = true;
    load(() => alive);
    return () => { alive = false; };
  }, [load]);

  useEffect(() => {
    if (!ship?.route_code) return;
    api.get("/booking/pickup-availability", { params: { route_code: ship.route_code } })
      .then(({ data }) => setAvail(data)).catch(() => setAvail(null));
  }, [ship?.route_code]);

  if (error && !draft) return <div className="max-w-3xl mx-auto p-6 text-sm text-red-600" data-testid="revise-load-error">{error.message}</div>;
  if (!rev || !draft) return <div className="max-w-3xl mx-auto p-6 text-sm" data-testid="revise-loading"><CircleNotch className="animate-spin inline" /> Loading booking…</div>;
  if (!rev.allowed) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-3" data-testid="revise-locked">
        <p className="text-sm">This booking is confirmed and can no longer be edited.</p>
        <Link to={`/sender/shipments/${id}`} className="btn-ghost text-sm" data-testid="revise-back-link">Back to shipment</Link>
      </div>
    );
  }

  const current = JSON.stringify(draft);
  const dirty = current !== original;
  const upd = (patch) => { setDraft((d) => ({ ...d, ...patch })); setPreview(null); setError(null); };
  const updRcv = (patch) => upd({ receiver: { ...draft.receiver, ...patch } });
  const setPkg = (i, k, v) => upd({ packages: draft.packages.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) });
  const choices = avail ? [avail.today, avail.next_available].filter(Boolean) : [];
  const slotChoice = choices.find((c) => c.date === draft.service_date);

  const body = () => ({ ...draft, promo_code: draft.promo_code || null, expected_quote_version: rev.expected_quote_version });

  const handleErr = (e) => {
    const det = e?.response?.data?.detail;
    const code = det && typeof det === "object" ? det.code : undefined;
    if (det?.pickup_reselect) {
      setDraft((d) => ({ ...d, preferred_pickup_window: "" }));
      setError({ code: "PICKUP", message: det.message || "Your pickup time is no longer available. Please select a new pickup time." });
    } else if (Array.isArray(det) && det.length) {
      const f = det[0];
      setError({ code: "VALIDATION", message: `Please check ${(f.loc || []).slice(1).join(" › ").replace(/_/g, " ")}: ${f.msg}` });
    } else {
      setError({ code, message: friendlyError(e, "We couldn't update this booking. Please try again.") });
    }
  };

  const onPreview = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/booking/${id}/revise-draft/preview`, body(), { timeout: 90000 });
      setPreview(data); setPreviewedFor(current);
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const onSave = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/booking/${id}/revise-draft`, body(), { timeout: 90000 });
      toast.success("Booking updated. Please review and accept the revised quote.");
      nav(`/sender/shipments/${id}`);
    } catch (e) { handleErr(e); setPreview(null); } finally { setBusy(false); }
  };

  const q = preview?.quote;
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="revise-booking-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">EDIT BOOKING</div>
          <div className="id-chip id-chip-strong mt-1" data-testid="revise-shipment-number">{ship.public_shipment_number}</div>
        </div>
        <Link to={`/sender/shipments/${id}`} className="btn-ghost text-sm" data-testid="revise-cancel-link">Cancel</Link>
      </div>
      <p className="text-sm text-[hsl(var(--muted))]" data-testid="revise-intro">
        Saving changes recalculates your quote as a new version. You'll need to accept the new quote before paying.
        To change only the sender contact mobile, use Edit next to Sender contact instead — your quote stays the same.
      </p>

      <Section title="PICKUP ADDRESS">
        <AddressFields v={draft.pickup_address} onChange={(pickup_address) => upd({ pickup_address })} prefix="rev-pickup" />
      </Section>
      <Section title="DELIVERY ADDRESS">
        <AddressFields v={draft.receiver.delivery_address} onChange={(delivery_address) => updRcv({ delivery_address })} prefix="rev-delivery" />
      </Section>

      <Section title="PACKAGE DETAILS">
        <div className="space-y-3">
          {draft.packages.map((p, i) => (
            <div key={i} className="border border-[hsl(var(--border))] rounded-[2px] p-3" data-testid={`rev-pkg-${i}`}>
              <div className="flex justify-end">
                {draft.packages.length > 1 && (
                  <button type="button" onClick={() => upd({ packages: draft.packages.filter((_, idx) => idx !== i) })} aria-label="Remove package type" data-testid={`rev-pkg-${i}-remove`}><Trash size={14} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                {[["actual_weight_kg_per_box", "Weight/box (kg)"], ["quantity", "Quantity"], ["length_cm", "L (cm)"], ["width_cm", "W (cm)"], ["height_cm", "H (cm)"]].map(([k, label]) => (
                  <Field key={k} label={label}>
                    <input type="number" min="0" className="input-field" value={p[k] ?? ""} onChange={(e) => setPkg(i, k, e.target.value)} data-testid={`rev-pkg-${i}-${k}`} />
                  </Field>
                ))}
                <Field label="Package type">
                  <select className="input-field" value={p.package_type} onChange={(e) => setPkg(i, "package_type", e.target.value)} data-testid={`rev-pkg-${i}-type`}>
                    {PACKAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="btn-ghost mt-3" data-testid="rev-add-pkg"
          onClick={() => upd({ packages: [...draft.packages, { quantity: "", actual_weight_kg_per_box: "", length_cm: "", width_cm: "", height_cm: "", package_type: "CARTON_BOX" }] })}>
          <Plus size={12} /> Add another package type
        </button>
      </Section>

      <Section title="INVOICE, GOODS & ASSISTANCE">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Declared invoice value (₹)"><input type="number" className="input-field" value={draft.declared_invoice_value ?? ""} onChange={(e) => upd({ declared_invoice_value: e.target.value })} data-testid="rev-invoice-value" /></Field>
          <Field label="Goods description"><input className="input-field" value={draft.goods_description || ""} onChange={(e) => upd({ goods_description: e.target.value })} data-testid="rev-goods" /></Field>
          <Field label="Commodity category">
            <select className="input-field" value={draft.commodity_category} onChange={(e) => upd({ commodity_category: e.target.value })} data-testid="rev-commodity">
              {[...new Set([draft.commodity_category, ...COMMODITY_CATEGORIES])].map((c) => <option key={c} value={c}>{String(c).replace(/_/g, " ")}</option>)}
            </select>
          </Field>
        </div>
        <div className="overline mt-3 mb-1.5">RESTRICTED GOODS DECLARATION</div>
        <div className="flex flex-wrap gap-2">
          {RESTRICTED_FLAGS.map((f) => (
            <label key={f} className="inline-flex items-center gap-1.5 text-xs border px-2 py-1 rounded-sm cursor-pointer">
              <input type="checkbox" checked={draft.restricted_goods_declared.includes(f)} data-testid={`rev-restricted-${f}`}
                onChange={(e) => upd({ restricted_goods_declared: e.target.checked ? [...draft.restricted_goods_declared, f] : draft.restricted_goods_declared.filter((x) => x !== f) })} />
              {f.replace(/_/g, " ")}
            </label>
          ))}
        </div>
        <div className="overline mt-3 mb-1.5">LOADING & UNLOADING ASSISTANCE</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {LOAD_ASSIST_OPTIONS.map((o) => (
            <button key={o.value} type="button" onClick={() => upd({ load_assist_side: o.value })} data-testid={`rev-load-assist-${o.value}`}
              className={`text-sm rounded-[2px] border px-3 py-2 text-left ${draft.load_assist_side === o.value ? "border-[hsl(var(--accent))] bg-[hsl(var(--info-bg))] font-medium" : "border-[hsl(var(--border))]"}`}>
              {o.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="PICKUP">
        <div className="flex flex-col gap-2" data-testid="rev-pickup-dates">
          {choices.map((c) => (
            <label key={c.date} className="flex items-center gap-2 text-sm">
              <input type="radio" name="rev-pickup-date" checked={draft.service_date === c.date} data-testid={`rev-pickup-date-${c.date}`}
                onChange={() => upd({ service_date: c.date, preferred_pickup_window: "" })} />
              <span>{c.label}</span>
            </label>
          ))}
          {!slotChoice && draft.service_date && <p className="text-xs text-[hsl(var(--muted))]" data-testid="rev-pickup-current">Current pickup: {draft.service_date} {draft.preferred_pickup_window}</p>}
        </div>
        <select className="input-field mt-2" value={draft.preferred_pickup_window || ""} disabled={!slotChoice} data-testid="rev-pickup-time"
          onChange={(e) => upd({ preferred_pickup_window: e.target.value })}>
          <option value="">{slotChoice ? "Select a pickup time" : "Select a pickup date"}</option>
          {[...new Set([...(slotChoice ? slotChoice.slots : []), ...(draft.preferred_pickup_window ? [draft.preferred_pickup_window] : [])])].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <p className="text-xs text-[hsl(var(--muted))] mt-1">Pickup times are in India Standard Time (IST). The pickup time is checked again when you review the price.</p>
      </Section>

      <Section title="RECEIVER">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name"><input className="input-field" value={draft.receiver.name || ""} onChange={(e) => updRcv({ name: e.target.value })} data-testid="rev-receiver-name" /></Field>
          <Field label="Company (optional)"><input className="input-field" value={draft.receiver.company || ""} onChange={(e) => updRcv({ company: e.target.value || null })} data-testid="rev-receiver-company" /></Field>
          <Field label="Mobile"><PhoneField value={draft.receiver.mobile || ""} onChange={(v) => updRcv({ mobile: v })} testid="rev-receiver-mobile" /></Field>
          <Field label="Email"><input type="email" className="input-field" value={draft.receiver.email || ""} onChange={(e) => updRcv({ email: e.target.value })} data-testid="rev-receiver-email" /></Field>
        </div>
      </Section>

      <Section title="PROMO CODE">
        <div className="flex gap-2 items-center">
          <input className="input-field" value={draft.promo_code || ""} placeholder="No promo" data-testid="rev-promo-input"
            onChange={(e) => upd({ promo_code: e.target.value.toUpperCase() || null })} />
          {draft.promo_code && <button type="button" className="btn-ghost text-sm" onClick={() => upd({ promo_code: null })} data-testid="rev-promo-remove">Remove</button>}
        </div>
        <p className="text-xs text-[hsl(var(--muted))] mt-1" data-testid="rev-promo-note">
          A promo code is checked again when the quote is recalculated. If it is no longer available you can remove it; removing it or changing it to a different code gives up the current code's discount.
        </p>
      </Section>

      {error && (
        <div className="text-sm text-red-600" data-testid="revise-error">
          {error.message}
          {error.code === "DRAFT_STALE" && <button type="button" className="ml-2 underline" onClick={() => load()} data-testid="revise-reload-latest">Reload latest</button>}
          {error.code === "DRAFT_LOCKED" && <Link className="ml-2 underline" to={`/sender/shipments/${id}`} data-testid="revise-locked-back">Back to shipment</Link>}
        </div>
      )}

      {q && previewedFor === current && (
        <Section title="REVISED QUOTE (NOT YET SAVED)">
          <dl className="divide-y divide-[hsl(var(--border))] text-sm" data-testid="revise-preview">
            {q.previous_payable != null && <Row k="Current quote total" v={`₹${inr(q.previous_payable)}`} testid="revise-preview-previous" />}
            {Number(q.concierge_service_fee) > 0 && <Row k="Concierge service fee (unchanged)" v={`₹${inr(q.concierge_service_fee)}`} testid="revise-preview-concierge" />}
            {q.promo_code && <Row k={`Promo (${q.promo_code})`} v={`− ₹${inr(q.promo_discount)}`} testid="revise-preview-promo" />}
            <Row k="Service subtotal (before insurance & GST)" v={`₹${inr(q.subtotal_excl_insurance)}`} testid="revise-preview-subtotal" />}
            <Row k="Insurance" v={`₹${inr(q.insurance_premium)}`} testid="revise-preview-insurance" />
            <Row k="GST" v={`₹${inr(q.gst)}`} testid="revise-preview-gst" />
            <Row k="Revised total payable" v={`₹${inr(q.payable)}`} testid="revise-preview-payable" strong />
          </dl>
          <ul className="text-xs text-[hsl(var(--muted))] mt-3 list-disc pl-4 space-y-1" data-testid="revise-consequences">
            <li>This creates quote version {q.version}. Your earlier accepted quote stays on record unchanged.</li>
            <li>You'll need to accept the revised quote before payment.</li>
            {preview.voids_pending_checkout && <li data-testid="revise-voids-checkout">The payment you started will be cancelled; you'll start a fresh payment for the new amount.</li>}
          </ul>
        </Section>
      )}

      <div className="flex justify-end gap-2">
        {!dirty && <span className="text-xs text-[hsl(var(--muted))] self-center" data-testid="revise-no-changes">No changes yet</span>}
        <button className="btn-ghost" disabled={busy || !dirty} onClick={onPreview} data-testid="revise-preview-button">
          {busy && !preview ? <><CircleNotch size={14} className="animate-spin" /> Calculating…</> : "Review revised price"}
        </button>
        <button className="btn-primary" disabled={busy || !dirty || !preview || previewedFor !== current} onClick={onSave} data-testid="revise-save-button">
          {busy && preview ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : "Save revision"}
        </button>
      </div>
    </div>
  );
}
